import {
    Button,
    Input,
    MultiSelect,
    NumberInput,
    Select,
    Textarea,
} from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation, useQuery } from "@tanstack/react-query";
import Common from "@utilities/common";
import { Form, Formik, FormikHelpers } from "formik";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IFormData } from "./types";
import { Schema } from "./validations";
import Editor from "@components/editor";

const FormContainer: FC = () => {
    const [initialValues] = useState<IFormData>({
        enName: "",
        faName: "",
        seoName: "",
        url: "",
        price: 0,
        brands: [],
        categories: [],
        tags: [],
        code: "",
        lcdSize: "",
        platformId: null,
        description: "",
        seoCanonical: "",
        seoDescription: "",
        seoKeywords: "",
    });
    const [platformQuery] = useState("");
    const [brandsQuery, setBrandsQuery] = useState("");
    const [categoriesQuery, setCategoriesQuery] = useState("");
    const [tagsQuery, setTagsQuery] = useState("");

    const Navigate = useNavigate();

    const { data: platformsData } = useQuery(
        [
            "getPlatforms",
            {
                query: platformQuery,
            },
        ],
        () =>
            Common.api.platforms.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { data: tagsData } = useQuery(
        [
            "getTags",
            {
                query: tagsQuery,
            },
        ],
        () => Common.api.tags.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { data: brandsData } = useQuery(
        [
            "getBrands",
            {
                query: brandsQuery,
            },
        ],
        () => Common.api.brands.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { data: categoriesData } = useQuery(
        [
            "getCategories",
            {
                query: categoriesQuery,
            },
        ],
        () =>
            Common.api.categories.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { mutate, isLoading } = useMutation(
        ({
            faName,
            enName,
            seoName,
            url,
            price,
            brands,
            categories,
            tags,
            code,
            lcdSize,
            platformId,
            description,
            seoCanonical,
            seoDescription,
            seoKeywords,
        }: IFormData) =>
            Common.api.products.Create({
                faName,
                enName,
                seoName,
                url,
                price,
                brands,
                categories,
                tags,
                ...(platformId !== null ? { platformId } : {}),
                ...(code !== undefined ? { code } : {}),
                ...(lcdSize !== undefined ? { lcdSize } : {}),
                ...(description !== undefined ? { description } : {}),
                ...(seoCanonical !== undefined ? { seoCanonical } : {}),
                ...(seoDescription !== undefined ? { seoDescription } : {}),
                ...(seoKeywords !== undefined ? { seoKeywords } : {}),
            }),
        {
            onSuccess: () => {
                Navigate(RoutesInstance.products);
            },
        },
    );

    const handleSubmit = async (
        values: IFormData,
        formik: FormikHelpers<IFormData>,
    ) => {
        mutate(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Schema}
            validateOnBlur={true}
            validateOnChange={true}>
            {({ values, errors, handleChange, handleBlur, isValid }) => (
                <Form>
                    <div className="mb-4">
                        <Input.Wrapper
                            id="faName"
                            label="نام فارسی"
                            required
                            error={errors.faName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="faName"
                                name="faName"
                                type="text"
                                placeholder="نام فارسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.faName}
                                invalid={!!errors.faName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="enName"
                            label="نام انگلیسی"
                            required
                            error={errors.enName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="enName"
                                name="enName"
                                type="text"
                                placeholder="نام انگلیسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.enName}
                                invalid={!!errors.enName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="seoName"
                            label="نام سئو"
                            required
                            error={errors.seoName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoName"
                                name="seoName"
                                type="text"
                                placeholder="نام سئو"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoName}
                                invalid={!!errors.seoName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="url"
                            label="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                            required
                            error={errors.url}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="url"
                                name="url"
                                type="text"
                                placeholder="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.url}
                                invalid={!!errors.url}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <NumberInput
                            variant="filled"
                            color="blue"
                            id="price"
                            name="price"
                            placeholder="مبلغ"
                            label="مبلغ"
                            required
                            error={errors.price}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            value={values.price}
                        />
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                tagsData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.tags.map((item) => item.toString())}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "tags",
                                        name: "tags",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="برچسب ها"
                            placeholder="انتخاب برچسب"
                            searchable
                            searchValue={tagsQuery}
                            onSearchChange={(query) => setTagsQuery(query)}
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                brandsData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.brands.map((item) => item.toString())}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "brands",
                                        name: "brands",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="برند ها"
                            placeholder="انتخاب برند"
                            searchable
                            searchValue={brandsQuery}
                            onSearchChange={(query) => setBrandsQuery(query)}
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                categoriesData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.categories.map((item) =>
                                item.toString(),
                            )}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "categories",
                                        name: "categories",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="دسته بندی ها"
                            placeholder="انتخاب دسته بندی"
                            searchable
                            searchValue={categoriesQuery}
                            onSearchChange={(query) =>
                                setCategoriesQuery(query)
                            }
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-4">
                        <Select
                            variant="filled"
                            data={[
                                {
                                    label: "انتخاب پلتفرم",
                                    value: "",
                                },
                                ...(platformsData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []),
                            ]}
                            label="پلتفرم"
                            placeholder="انتخاب پلتفرم"
                            value={values.platformId?.toString()}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "platformId",
                                        name: "platformId",
                                        value: value ? value : null,
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                        />
                    </div>

                    <div className="mb-4">
                        <Editor
                            value={values.description}
                            handleChange={(value: string) =>
                                handleChange({
                                    target: {
                                        id: "description",
                                        name: "description",
                                        value,
                                    },
                                })
                            }
                        />
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="seoKeywords"
                            label="لینک Canonical"
                            error={errors.seoCanonical}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoCanonical"
                                name="seoCanonical"
                                type="text"
                                className="direction-ltr"
                                placeholder="لینک Canonical"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoCanonical}
                                invalid={!!errors.seoCanonical}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="seoDescription"
                            name="seoDescription"
                            placeholder="توضیحات سئو"
                            label="توضیحات سئو"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            error={errors.seoDescription}
                            value={values.seoDescription}
                        />
                    </div>

                    <div className="mb-10">
                        <Input.Wrapper
                            id="seoKeywords"
                            label="کلمات کلیدی سئو"
                            error={errors.seoKeywords}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoKeywords"
                                name="seoKeywords"
                                type="text"
                                placeholder="کلمات کلیدی سئو"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoKeywords}
                                invalid={!!errors.seoKeywords}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="text-center">
                        <Button
                            variant="filled"
                            className="transition-all duration-300 px-16"
                            color="blue"
                            radius={9999}
                            type="submit"
                            disabled={!isValid || isLoading}
                            loading={isLoading}>
                            ثبت
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FormContainer;
